import { usePage } from "@inertiajs/react";
import { Transition } from "@headlessui/react";
import { useEffect, useState } from "react";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export default function Toast() {
  const { flash } = usePage();

  const [toast, setToast] = useState<{
    open: boolean;
    message: string | undefined;
    severity: "info" | "error";
  }>({ open: false, message: "", severity: "info" });

  useEffect(() => {
    if (flash.notice) {
      setToast({
        open: true,
        message: flash.notice,
        severity: "info",
      });
    } else if (flash.alert) {
      setToast({
        open: true,
        message: flash.alert,
        severity: "error",
      });
    }
  }, [flash]);

  useEffect(() => {
    if (!toast.open) return;
    const timer = setTimeout(() => setToast((t) => ({ ...t, open: false })), 3000);
    return () => clearTimeout(timer);
  }, [toast.open]);

  const handleClose = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 ml-[250px]">
        <Transition
          show={toast.open}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:data-[enter]:translate-y-2 data-[enter]:transform data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-x-2 data-[closed]:data-[enter]:sm:translate-y-0">
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">
                  {toast.severity === "info" && (
                    <CheckCircleIcon aria-hidden="true" className="h-6 w-6 text-green-400" />
                  )}
                  {toast.severity === "error" && (
                    <ExclamationTriangleIcon aria-hidden="true" className="h-6 w-6 text-orange-400" />
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900">{toast.message}</p>
                </div>
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}
